import { Injectable, inject } from '@angular/core'
import { TimeTracking } from '@awork/features/time-tracking/models/time-tracking.model'
import { ProjectStore } from '@awork/features/project/state/project.store'
import { TaskQuery } from '@awork/features/task/state/task.query'
import { TaskStore } from '@awork/features/task/state/task.store'
import { EntitySignalStore } from '@awork/core/state/signal-store/entitySignalStore'
import { removeUnwantedProperties } from '@awork/core/state/signal-store/helpers'

// Old properties from V1 to be removed
const AVOID_PROPERTIES = [
  'startDateUtc',
  'endDateUtc',
  'endDateLocal',
  'startDateLocal',
  'startTimeUtc',
  'endTimeUtc',
  'startTimeLocal',
  'endTimeLocal'
]

@Injectable({ providedIn: 'root' })
export class TimeTrackingStore extends EntitySignalStore<TimeTracking> {
  private projectStore = inject(ProjectStore)
  private taskQuery = inject(TaskQuery)
  private taskStore = inject(TaskStore)

  constructor() {
    super({
      name: 'timeTracking',
      // Filters use stored local fields. Query copies rebuild them so derived days can follow a view timezone.
      entityConstructor: timeTracking => new TimeTracking(removeUnwantedProperties(timeTracking, AVOID_PROPERTIES)),
      nullableProps: [
        'endDate',
        'duration',
        'breakDuration',
        'userId',
        'user',
        'taskId',
        'task',
        'projectId',
        'project'
      ]
    })
  }

  preUpdateEntity(prevEntity: TimeTracking, nextEntity: Partial<TimeTracking>): Partial<TimeTracking> {
    const modifiedOn = new Date().getTime()

    // Removes old properties not to save them in the store and creates a new time tracking to regenerate the real values
    const cleanedEntity = removeUnwantedProperties(nextEntity, AVOID_PROPERTIES)

    //TODO: Eliminate the instantiation of new TimeTracking objects after the UI migration is complete to enhance performance.
    const purgedNextEntity = new TimeTracking(cleanedEntity)

    return {
      ...purgedNextEntity,
      modifiedOn
    }
  }

  preAddEntity(newEntity: TimeTracking): TimeTracking {
    const modifiedOn = new Date().getTime()

    // Removes old properties not to save them in the store and creates a new time tracking to regenerate the real values
    const cleanedEntity = removeUnwantedProperties(newEntity, AVOID_PROPERTIES)

    //TODO: Eliminate the instantiation of new TimeTracking objects after the UI migration is complete to enhance performance.
    const purgedNewEntity = new TimeTracking(cleanedEntity)

    return {
      ...purgedNewEntity,
      modifiedOn
    } as TimeTracking
  }

  /**
   * Sets the current time tracking in the store.
   * Only active time trackings are allowed as current active entity.
   * If the provided time tracking is missing or inactive, the current active id is cleared.
   * @param {TimeTracking | null} timeTracking
   */
  setCurrentTimeTracking(timeTracking: TimeTracking | null): void {
    if (timeTracking?.isActive) {
      this.setActive(timeTracking.id)

      return
    }

    this.setActive(null)
  }

  /**
   * Updates the time tracking's project and/or task tracked duration
   * @param {TimeTracking} timeTracking
   * @param {TimeTracking} storedTimeTracking
   * @param {boolean} [deletion=false] True if a time tracking is being deleted
   */
  updateEntityTrackedDuration(timeTracking: TimeTracking, storedTimeTracking: TimeTracking, deletion = false): void {
    let deltaDuration = deletion ? 0 : timeTracking.duration

    if (storedTimeTracking && storedTimeTracking.duration) {
      deltaDuration -= storedTimeTracking.duration
    }

    if (timeTracking.projectId) {
      this.projectStore.update(
        project => project.id === timeTracking.projectId,
        project => ({
          trackedDuration: (project.trackedDuration ? project.trackedDuration : 0) + deltaDuration
        })
      )
    }

    if (timeTracking.taskId) {
      const taskId = timeTracking.taskId
      const parentTaskId = timeTracking.task?.parentId || this.taskQuery.getTask(taskId)?.parentId

      this.taskStore.update(
        task => task.id === taskId,
        task => {
          const trackedDuration = (task.trackedDuration ?? 0) + deltaDuration

          return parentTaskId
            ? { trackedDuration }
            : { trackedDuration, totalTrackedDuration: (task.totalTrackedDuration ?? 0) + deltaDuration }
        }
      )

      if (parentTaskId) {
        this.taskStore.update(
          task => task.id === parentTaskId,
          task => ({ totalTrackedDuration: (task.totalTrackedDuration ?? 0) + deltaDuration })
        )
      }
    }
  }

  /**
   * Updates the entity's time trackings
   * @param {'task' | 'project'} entityType
   * @param {string} entityId
   * @param {'delete' | 'unlink'} operation
   */
  updateEntityTimeTrackings(entityType: 'task' | 'project', entityId: string, operation: 'delete' | 'unlink'): void {
    if (operation === 'delete') {
      this.remove(
        timeTracking => !timeTracking.isBilled && this.isEntityTimeTracking(timeTracking, entityType, entityId)
      )
      // In case there are time trackings that were not deleted because they are billed, then we need to unlink them
      this.updateEntityTimeTrackings(entityType, entityId, 'unlink')
    } else if (operation === 'unlink') {
      this.update(timeTracking => this.isEntityTimeTracking(timeTracking, entityType, entityId), {
        projectId: null,
        project: null,
        taskId: null,
        task: null
      })
    }
  }

  /**
   * Returns true is if the timetracking belongs to the entity
   * @param {Readonly<TimeTracking>} timeTracking
   * @param {'task' | 'project'} entityType
   * @param {string} entityId
   * @returns {boolean}
   */
  private isEntityTimeTracking(
    timeTracking: Readonly<TimeTracking>,
    entityType: 'task' | 'project',
    entityId: string
  ): boolean {
    return (
      (entityType === 'project' && timeTracking.projectId === entityId) ||
      (entityType === 'task' && timeTracking.taskId === entityId)
    )
  }
}
